import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { api, apiClient } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import type { ApiEnvelope } from '../../lib/api-envelope';
import { fmtCurrency } from '../../lib/display-labels';
import { formatInstantDay, instantTenantDay } from '../../lib/calendar-date';
import {
  CASING_BILLING_VERTICAL,
  OTHER_BILLING_VERTICALS,
  OTHER_BILLING_VERTICAL_KEYS,
  fetchAllUnifiedInvoices,
  isOtherBillingVertical,
} from '../../lib/finance-invoice-verticals';
import type { OtherBillingVertical } from '../../lib/finance-invoice-verticals';
import type { UnifiedInvoiceRow } from '../../types/unified-invoice';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useInvoicePermissions } from '../../hooks/useInvoicePermissions';
import NewInvoiceModal, { MANUAL_INVOICE_CREATION_ENABLED } from '../../components/invoices/NewInvoiceModal';
import {
  qboConnectionFault,
  readQboErrorMessage,
  QBO_FAULT_TITLE,
  QBO_FAULT_MESSAGE,
  QBO_FAULT_CTA,
  QBO_RECONNECT_ROUTE,
  readQboPushRefusal,
  qboPushSuccessMessage,
  qboMappingTabHref,
} from '../../lib/qbo-errors';
import type { QboConnectionFault, QboPushRefusal } from '../../lib/qbo-errors';
import { useQboReadiness, qboPushGate, QBO_READINESS_KEY } from '../../hooks/useQboReadiness';
import { QboPushRefusalNotice, QboPushOffReason } from '../../components/invoices/QboPushNotices';
import type { QboPushResult } from '../../types/qbo-mapping';
import { downloadInvoiceCsv } from '../../lib/invoice-csv-export';
import { toast } from 'sonner';
import {
  FileText,
  Send,
  DollarSign,
  Bot,
  Calendar,
  Plus,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronRight,
  X as XIcon,
  Download,
  Loader2,
} from 'lucide-react';
import { Tooltip } from '../../components/ui/tooltip';

/* ── types ── */

/**
 * The statuses the API has, minus void. There is no "Pending AI Review" tab:
 * it listed `status=pending_ai_review`, which `public.invoice_status` has no
 * member for, so it was empty by construction — and it was the tab the screen
 * opened on. Its Approve / Reject / bulk controls called
 * POST /invoices/:id/approve, /:id/reject and /invoices/batch-action, none of
 * which exist under any name (PATCH /invoices/lines/:id/approve acts on a
 * draft LINE, a different record with a different id). No route lists AI
 * drafts, so there is nothing for such a tab to show until one is built.
 */
type TabKey = 'draft' | 'finalized' | 'sent' | 'paid' | 'overdue' | 'approved' | 'all' | 'void';

/** The tabs GET /invoices is counted on — the casing set, exactly as before. */
const CASING_TAB_KEYS: TabKey[] = ['draft', 'finalized', 'sent', 'paid', 'overdue'];

interface Invoice {
  id: string;
  invoice_number: string;
  customer_name: string;
  source_types: string[];
  /** As stored: a decimal string, a number, or absent. Never coerced to 0. */
  total: number | string | null;
  status: string;
  created_at: string;
  /** The API's balance_due. Mark Paid records exactly this; absent means no payment is offered. */
  balance_due: number | string | null;
  ai_suggested?: boolean;
  qbo_invoice_id?: string | null;
  /** companies.enabled_verticals key. Casing rows (GET /invoices) are `casing_running`. */
  vertical: string;
  /** Casing rows open /invoices/:id. Null: no detail screen for this vertical — not clickable. */
  detail_path: string | null;
  /** Non-casing rows only: the client filter matches on it (GET /invoices filters casing server-side). */
  customer_id?: string | null;
  /** Non-casing rows only: the view's currency. Absent renders CAD. */
  currency?: string | null;
}

/**
 * `/invoices/summary/dashboard`, as the API sends it: snake_case, and every
 * figure a STRING — COUNT(*) is bigint and SUM() is numeric, and node-postgres
 * returns both as text. displayValue() and fmtCurrency() take strings, so
 * nothing here parses them.
 */
interface InvoiceSummary {
  draft_count?: string | number | null;
  sent_count?: string | number | null;
  paid_count?: string | number | null;
  overdue_count?: string | number | null;
  outstanding_total?: string | number | null;
  paid_this_month?: string | number | null;
}

/* ── constants ── */

const TABS: { key: TabKey; label: string }[] = [
  { key: 'draft', label: 'Draft' },
  { key: 'finalized', label: 'Saved' },
  { key: 'sent', label: 'Sent' },
  { key: 'paid', label: 'Paid' },
  { key: 'overdue', label: 'Overdue' },
];

/**
 * Downhole, Pressure Testing and Trucking store `approved` (customer-approved,
 * not yet paid); casing has no such status. The tab is shown only to a tenant
 * with one of those verticals, so a casing tenant's tabs are unchanged.
 */
const APPROVED_TAB: { key: TabKey; label: string } = { key: 'approved', label: 'Approved' };

/**
 * All and Void, for an NDT (or, since PF-5c, Pipeline) tenant without casing (Jason, 2026-10-24: the page
 * opened on Draft with no way to see every invoice or a voided one). Those rows
 * come from the view and are filtered here, so neither tab asks GET /invoices.
 * Every other tenant keeps the tabs and the Draft default it had: a casing
 * tenant's tabs are counted by GET /invoices?status=, and the other verticals'
 * screens and tests open on Draft.
 */
const ALL_TAB: { key: TabKey; label: string } = { key: 'all', label: 'All' };
const VOID_TAB: { key: TabKey; label: string } = { key: 'void', label: 'Void' };

const SOURCE_BADGE: Record<string, { label: string; className: string }> = {
  bol: { label: 'BOL', className: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  weight_ticket: { label: 'WT', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  wt: { label: 'WT', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  rental: { label: 'Rental', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  field_ticket: { label: 'FT', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  manual: { label: 'Manual', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  // PT-6 (fieldiq-api#939): unified_invoices_view's pressure-testing branch.
  pt_field_ticket: { label: 'Pressure Testing', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  ndt_field_ticket: { label: 'NDT', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  pf_field_ticket: { label: 'Pipeline', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  wt_field_ticket: { label: 'Well Testing', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  ws_field_ticket: { label: 'Well Servicing', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  ew_invoice: { label: 'Earthworks', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  // The other verticals' unified_invoices_view rows, one badge per vertical.
  downhole_run: { label: 'Downhole', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  trucking: { label: 'Trucking', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  rental_billing: { label: 'Rentals', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
};

/**
 * The register's status pill and row rail: dark ink on a saturated signal
 * fill, the Quotes / Rate Sheets / Equipment badge.
 *
 * EXHAUSTIVE over `public.invoice_status`, which has exactly six members:
 * draft, finalized, sent, paid, overdue, void (schema.ts invoiceStatusEnum).
 * `pending_ai_review` is NOT one of them — the API cannot produce a row with
 * that status, so it has no entry here (and no tab; see TabKey).
 *
 * SIX STATUSES, FIVE SIGNAL HUES. Draft and Saved (`finalized`) share amber:
 * both are "not yet with the customer", and the label tells them apart. Sent
 * is blue, Paid green, Overdue red. Void is a dead row and takes slate on the
 * pill and the quiet steel edge on the rail, as a retired unit does.
 *
 * A value outside the enum reads `—` on slate with a transparent rail — never
 * a guessed label. The old text-colour map fell back to muted text echoing
 * the raw value.
 */
const STATUS_BADGE: Record<string, { label: string; fill: string; rail: string }> = {
  draft:     { label: 'Draft',   fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  finalized: { label: 'Saved',   fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  sent:      { label: 'Sent',    fill: 'bg-fi-signal-blue',  rail: 'border-l-fi-blue' },
  paid:      { label: 'Paid',    fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  overdue:   { label: 'Overdue', fill: 'bg-fi-signal-red',   rail: 'border-l-fi-red' },
  void:      { label: 'Void',    fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  // Not a casing status: Downhole / Pressure Testing / Trucking only. Blue as
  // Sent — with the customer, not yet paid — and the label tells them apart.
  approved:  { label: 'Approved', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
};

const UNKNOWN_STATUS = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

const statusSkin = (status: string | null | undefined) =>
  (status && Object.prototype.hasOwnProperty.call(STATUS_BADGE, status) && STATUS_BADGE[status]) || UNKNOWN_STATUS;

/* ── helpers ── */

/** @see fmtCurrency — absence renders '—', a real 0 renders '$0.00'. */
// Casing rows carry no currency and render CAD, as always; another vertical's row
// renders its own (Apex Downhole bills in USD).
const formatCurrency = (n: number | string | null | undefined, currency?: string | null) =>
  fmtCurrency(n, currency || 'CAD');

/** `created_at` is an instant: the TENANT's day it fell on, '' when absent or
 *  the zone is unknown (both call sites render '' as an em dash). */
function formatDate(iso: string, timeZone: string | null) {
  return formatInstantDay(iso, timeZone, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-CA') ?? '';
}

/* ── skeleton row ── */

function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 border-t border-fieldiq-border px-4 py-4 animate-pulse">
      <div className="h-4 w-20 bg-fieldiq-border rounded" />
      <div className="h-4 w-32 bg-fieldiq-border rounded" />
      <div className="h-4 w-16 bg-fieldiq-border rounded" />
      <div className="flex-1" />
      <div className="h-4 w-20 bg-fieldiq-border rounded" />
      <div className="h-4 w-16 bg-fieldiq-border rounded" />
    </div>
  );
}

/* ── badges ── */

/** An unmapped source reads `—`, never the raw value. */
function SourceBadge({ type }: { type: string }) {
  const key = String(type ?? '').toLowerCase();
  const cfg = (Object.prototype.hasOwnProperty.call(SOURCE_BADGE, key) && SOURCE_BADGE[key]) || { label: '—', className: 'bg-slate-500/20 text-slate-400 border-slate-500/30' };
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

function InvoiceStatusBadge({ status }: { status: string | null | undefined }) {
  const skin = statusSkin(status);
  return (
    <span
      data-testid="invoice-status-badge"
      className={`inline-flex w-fit items-center rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/* ── main component ── */

export default function Invoices() {
  const navigate = useNavigate();
  const { hasVertical } = useAuth();
  const timeZone = useTenantTimeZone();
  // Per action, not per screen: create/send/payment, the draft review, and the
  // QuickBooks push are three different API gates. See useInvoicePermissions.
  const perms = useInvoicePermissions();

  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // The tenant's billing verticals. A tenant with NO other billing vertical
  // reads GET /invoices exactly as this screen always has — casing tenants,
  // and a tenant whose verticals have not resolved — so `showsCasing` is the
  // casing key OR the absence of any other.
  const otherVerticals = OTHER_BILLING_VERTICAL_KEYS.filter(v => hasVertical(v));
  const showsCasing = hasVertical(CASING_BILLING_VERTICAL) || otherVerticals.length === 0;
  const billingVerticalCount = otherVerticals.length + (showsCasing ? 1 : 0);

  // All + Void, and All as the default, for an NDT or Pipeline tenant without casing only (see ALL_TAB).
  const ndtTabs = (hasVertical('ndt_inspection') || hasVertical('pipeline_facility')) && !showsCasing;
  const [activeTab, setActiveTab] = useState<TabKey>(() => (ndtTabs ? 'all' : 'draft'));
  // `?vertical=<key>` is the deep link the per-vertical invoice screens use.
  const [verticalFilter, setVerticalFilter] = useState<string>(() => {
    const v = searchParams.get('vertical');
    return v === CASING_BILLING_VERTICAL || isOtherBillingVertical(v) ? v : 'all';
  });
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [actionError, setActionError] = useState<{ title: string; detail: string } | null>(null);
  const [pushingQbo, setPushingQbo] = useState<string | null>(null);
  const [qboError, setQboError] = useState<{ title: string; detail: string; fault: QboConnectionFault | null } | null>(null);
  // Refusals with a reason the office can read (API #887) — a panel, never the
  // red fault banner above and never the success toast.
  const [qboRefusal, setQboRefusal] = useState<QboPushRefusal | null>(null);
  const qboReadiness = useQboReadiness();
  const pushGate = qboPushGate(qboReadiness);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [csvDownloading, setCsvDownloading] = useState(false);

  // Query params, spelled the way GET /invoices reads them (invoices.routes.ts):
  // `customerId`, and `fromDate` / `toDate` as calendar days. They were
  // `customer`, `date_from` and `date_to`, which the route never read, so
  // both filters silently returned the unfiltered list. No `vertical`: the
  // route does not read one — it IS the casing list — so the vertical chips
  // decide whether it is asked at all (casingListed), not what it is asked.
  const params: Record<string, string> = { status: activeTab };
  if (customerFilter) params.customerId = customerFilter;
  if (dateFrom) params.fromDate = dateFrom;
  if (dateTo) params.toDate = dateTo;

  // Fetch counts for all tabs in parallel.
  //
  // A failed count is `undefined`, not 0. These counts used to render only
  // when positive, so a failure mapped to 0 was invisible; the chips now show
  // a real zero as `0`, and a request that failed must not borrow that
  // spelling. Same six requests, same keys.
  const { data: tabCounts } = useQuery({
    queryKey: ['invoice-tab-counts'],
    queryFn: async () => {
      const statuses: TabKey[] = ['draft', 'finalized', 'sent', 'paid', 'overdue'];
      const results = await Promise.all(
        statuses.map(s => apiClient.get('/invoices', { params: { status: s } })
          .then(r => ({ status: s, count: (r.data?.data ?? r.data ?? []).length as number | undefined }))
          .catch(() => ({ status: s, count: undefined as number | undefined }))
        )
      );
      return Object.fromEntries(results.map(r => [r.status, r.count])) as Record<TabKey, number | undefined>;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    enabled: showsCasing,
  });

  // The band's four figures, as the API computes them. Company-wide: the
  // endpoint takes no status, vertical, client or date parameter, so the
  // strip does not follow the filters below. HomeScreen already reads the
  // same endpoint (and its overdue auto-mark) on every load.
  //
  // No try/catch: a refused or failed request leaves `data` undefined and
  // every figure renders `—`, never a confident $0.00.
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['invoice-summary-dashboard'],
    queryFn: async () => {
      const res = await api.invoices.dashboard();
      return unwrap<InvoiceSummary>(res.data);
    },
    staleTime: 30_000,
    retry: false,
    // Casing figures. A tenant without casing gets no band stats at all rather
    // than a strip of casing zeroes (a cross-vertical summary is a follow-up).
    enabled: showsCasing,
  });

  const casingListed = showsCasing && (verticalFilter === 'all' || verticalFilter === CASING_BILLING_VERTICAL);

  const { data: invoicesRaw, isLoading: casingLoading, refetch: refetchCasing } = useQuery({
    queryKey: ['unified-invoices', params],
    queryFn: () => apiClient.get('/invoices', { params }).then(r => r.data?.data ?? r.data ?? []),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    enabled: casingListed,
  });

  // Every other billing vertical's invoices, all statuses, from the view. The
  // tabs, chips and filters below act on this set client-side; it is small
  // (8 rows estate-wide on 2026-10-08) and the view pages at 100.
  const {
    data: otherRaw,
    isLoading: otherLoading,
    isError: otherFailed,
    refetch: refetchOther,
  } = useQuery({
    queryKey: ['finance-invoices-other-verticals', otherVerticals],
    queryFn: async () => (await Promise.all(otherVerticals.map(fetchAllUnifiedInvoices))).flat(),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    enabled: otherVerticals.length > 0,
  });

  const isLoading = casingLoading || otherLoading;
  const refetch = () => {
    if (casingListed) refetchCasing();
    if (otherVerticals.length > 0) refetchOther();
  };

  // The other verticals' rows as this screen draws them, every status.
  const otherInvoices: Invoice[] = useMemo(() => {
    const list: UnifiedInvoiceRow[] = Array.isArray(otherRaw) ? otherRaw : [];
    return list
      .filter(row => isOtherBillingVertical(row.vertical))
      .map(row => {
        const cfg = OTHER_BILLING_VERTICALS[row.vertical as OtherBillingVertical];
        return {
          id: row.id,
          invoice_number: row.invoice_number || '',
          customer_name: row.customer_name || '',
          source_types: [cfg.badge],
          total: row.total ?? null,
          status: row.status || '',
          created_at: row.created_at || '',
          balance_due: row.balance_due ?? null,
          ai_suggested: false,
          qbo_invoice_id: null,
          vertical: row.vertical,
          detail_path: cfg.detailPath ? cfg.detailPath(row.id) : null,
          customer_id: row.customer_id,
          currency: row.currency,
        };
      });
  }, [otherRaw]);

  // The same filters GET /invoices applies server-side, applied here: the tab's
  // status, the vertical chip, the client, and the created day in the TENANT's
  // zone. An undeterminable day is outside any date range, never inside it.
  const otherVisible: Invoice[] = useMemo(() => otherInvoices.filter(inv => {
    if (activeTab !== 'all' && inv.status !== activeTab) return false;
    if (verticalFilter !== 'all' && inv.vertical !== verticalFilter) return false;
    if (customerFilter && inv.customer_id !== customerFilter) return false;
    if (dateFrom || dateTo) {
      const day = instantTenantDay(inv.created_at, timeZone);
      if (!day) return false;
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
    }
    return true;
  }), [otherInvoices, activeTab, verticalFilter, customerFilter, dateFrom, dateTo, timeZone]);

  const casingInvoices: Invoice[] = useMemo(() => {
    if (!casingListed) return [];
    const list = Array.isArray(invoicesRaw) ? invoicesRaw : [];
    return list.map((inv: any) => ({
      id: inv.id,
      invoice_number: inv.invoice_number || inv.invoiceNumber || '',
      customer_name: inv.customer_name || inv.customerName || '',
      source_types: inv.source_types || inv.sourceTypes || [],
      // `??`, not `||`, and no parseFloat(... '0'): a stored 0 stays 0 and
      // renders $0.00, an absent total stays absent and renders `—`.
      total: inv.total ?? inv.total_amount ?? inv.totalAmount ?? null,
      status: inv.status || '',
      created_at: inv.created_at || inv.createdAt || '',
      balance_due: inv.balance_due ?? inv.balanceDue ?? null,
      ai_suggested: inv.ai_suggested || inv.aiSuggested || false,
      qbo_invoice_id: inv.qbo_invoice_id || inv.qboInvoiceId || null,
      vertical: CASING_BILLING_VERTICAL,
      detail_path: `/invoices/${inv.id}`,
    }));
  }, [invoicesRaw, casingListed]);

  // The register. With no other-vertical row in view the casing list is
  // returned AS IS — same rows, same server order. Otherwise newest first
  // across both, a stable sort so equal instants keep their source order.
  const invoices: Invoice[] = useMemo(() => {
    if (otherVisible.length === 0) return casingInvoices;
    const at = (iso: string) => {
      const ms = Date.parse(iso);
      return Number.isFinite(ms) ? ms : -Infinity;
    };
    return [...casingInvoices, ...otherVisible].sort((a, b) => at(b.created_at) - at(a.created_at));
  }, [casingInvoices, otherVisible]);

  // Customer list for filter dropdown
  const { data: customersRaw } = useQuery({
    queryKey: ['invoice-customers'],
    queryFn: () => apiClient.get('/customers').then(r => r.data?.data ?? r.data ?? []),
    staleTime: 60_000,
  });
  const customers: { id: string; name: string }[] = useMemo(() => {
    const list = Array.isArray(customersRaw) ? customersRaw : [];
    return list.map((c: any) => ({ id: c.id, name: c.name || c.company_name || c.companyName || '' }));
  }, [customersRaw]);

  // Vertical filter chips — only for a tenant that bills in two or more
  // verticals; a single-vertical tenant has nothing to choose between.
  const verticalChips: { key: string; label: string }[] = billingVerticalCount >= 2
    ? [
        { key: 'all', label: 'All' },
        ...(showsCasing ? [{ key: CASING_BILLING_VERTICAL, label: 'Casing' }] : []),
        ...otherVerticals.map(v => ({ key: v, label: OTHER_BILLING_VERTICALS[v].label })),
      ]
    : [];

  // Tabs: the casing five, exactly, for a casing tenant. Saved (`finalized`)
  // is a casing status, so a tenant without casing does not get it; Approved
  // is not one, so only a tenant with a vertical that stores it does.
  const visibleTabs = [
    ...(ndtTabs ? [ALL_TAB] : []),
    ...TABS.filter(tab => tab.key !== 'finalized' || showsCasing),
    ...(otherVerticals.some(v => OTHER_BILLING_VERTICALS[v].usesApproved) ? [APPROVED_TAB] : []),
    ...(ndtTabs ? [VOID_TAB] : []),
  ];

  /**
   * Send and Mark Paid. A refused request lands in the alert below with the
   * API's own words — it used to go to console.error and nowhere else, so a
   * 400 looked exactly like a success that had not refreshed yet.
   */
  const handleAction = async (inv: Invoice, action: 'send' | 'mark_paid') => {
    setActionError(null);
    const label = inv.invoice_number || 'this invoice';
    if (action === 'mark_paid') {
      // The payment is the balance the API says is due, to the cent — never a
      // placeholder. It sent `amount: 0`, which /payment refuses on every call.
      const due = Number(inv.balance_due);
      if (inv.balance_due === null || inv.balance_due === '' || !Number.isFinite(due) || due <= 0) {
        setActionError({
          title: `Could not mark ${label} paid`,
          detail: 'No balance due is recorded on this invoice. Open it to record a payment.',
        });
        return;
      }
    }
    setActingOn(inv.id);
    try {
      // Neither body is read: success is the 2xx, and the list refetch shows
      // the result. `unknown` says so rather than implying a shape.
      if (action === 'send') {
        await apiClient.post<ApiEnvelope<unknown>>(`/invoices/${inv.id}/send`);
      } else {
        await apiClient.post<ApiEnvelope<unknown>>(`/invoices/${inv.id}/payment`, { amount: Number(inv.balance_due) });
      }
      refetch();
      // The chip counts and the band's figures move with a send or a payment.
      queryClient.invalidateQueries({ queryKey: ['invoice-tab-counts'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-summary-dashboard'] });
    } catch (err: any) {
      setActionError({
        title: action === 'send' ? `Could not send ${label}` : `Could not mark ${label} paid`,
        detail: err?.response?.data?.error || err?.message || 'The request failed.',
      });
    } finally {
      setActingOn(null);
    }
  };

  const handlePushToQbo = async (invoiceId: string) => {
    setPushingQbo(invoiceId);
    setQboError(null);
    setQboRefusal(null);
    try {
      const res = await api.quickbooks.pushInvoice(invoiceId);
      refetch();
      // `adopted` linked an invoice QuickBooks already held — not "Pushed".
      toast.success(qboPushSuccessMessage(unwrap<QboPushResult>(res.data)));
    } catch (err: unknown) {
      const refusal = readQboPushRefusal(err);
      if (refusal) {
        setQboRefusal(refusal);
        if (refusal.kind === 'already_pushed') refetch();
        if (refusal.kind === 'in_progress') setTimeout(() => refetch(), 15_000);
        // The button was enabled on a readiness read the server now disagrees
        // with, so that read is stale.
        if (refusal.kind === 'not_ready') queryClient.invalidateQueries({ queryKey: QBO_READINESS_KEY });
        return;
      }
      console.error('Push to QBO failed:', err);
      // A connection fault is the one failure the operator can act on themselves,
      // so it gets the route to the fix — and the two faults get different copy,
      // because "expired" is a lie to a tenant that never connected. Everything
      // else keeps the API's own message: it is the only thing that says which
      // invoice was rejected.
      const fault = qboConnectionFault(err);
      setQboError(
        fault
          ? { title: QBO_FAULT_TITLE[fault], detail: QBO_FAULT_MESSAGE[fault], fault }
          : {
              title: 'Push to QuickBooks failed',
              detail: readQboErrorMessage(err) ?? 'Failed to push to QuickBooks',
              fault: null,
            }
      );
    } finally {
      setPushingQbo(null);
    }
  };

  const openInvoice = (inv: Invoice) => {
    if (inv.detail_path) navigate(inv.detail_path);
  };
  const isCasingRow = (inv: Invoice) => inv.vertical === CASING_BILLING_VERTICAL;
  /**
   * These rows hold their own action buttons, so this is the Rate Sheets row,
   * not the Quotes one: no `role="button"` (it would make the buttons inside
   * presentational), and a key opens the invoice only when the ROW has focus —
   * Enter on Send is Send's.
   */
  const rowKeys = (inv: Invoice) => (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openInvoice(inv);
    }
  };
  /** A row with somewhere to go opens on click / Enter; a row without one (trucking, rentals) is not interactive. */
  const rowOpenProps = (inv: Invoice) => inv.detail_path
    ? { tabIndex: 0, onClick: () => openInvoice(inv), onKeyDown: rowKeys(inv) }
    : {};

  // Casing counts are GET /invoices' six list lengths, as before; the other
  // verticals' are tallied from their rows. Either half failing reads `—`.
  const countsReady = (!showsCasing || !!tabCounts) && (otherVerticals.length === 0 || (!!otherRaw || otherFailed));
  const chipCount = (key: TabKey): number | undefined => {
    let n = 0;
    if (showsCasing && (CASING_TAB_KEYS as string[]).includes(key)) {
      const c = tabCounts?.[key as keyof typeof tabCounts];
      if (c === undefined) return undefined;
      n += c;
    }
    if (otherVerticals.length > 0) {
      if (otherFailed || !otherRaw) return undefined;
      n += key === 'all' ? otherInvoices.length : otherInvoices.filter(inv => inv.status === key).length;
    }
    return n;
  };

  /**
   * THE SELECTION IS WHAT IS TICKED AND ON SCREEN — never an id from another
   * tab or filter. `selectedIds` survives a tab switch; this intersection is
   * what the export sends, so a row the reader can no longer see is never in
   * the file they download.
   *
   * It exists only to feed the CSV export, so it is shown only to the roles
   * that may export (canExportCsv).
   */
  // A tenant without casing has no exportable row, so no checkbox column at all.
  const selectable = perms.canExportCsv && showsCasing;
  // The CSV export is GET /invoices' — casing rows only carry a checkbox.
  const selectableRows = invoices.filter(isCasingRow);
  const selectedVisible = selectableRows.filter(inv => selectedIds.has(inv.id));
  const allVisibleSelected = selectableRows.length > 0 && selectedVisible.length === selectableRows.length;
  const toggleSelected = (id: string) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  /**
   * One QuickBooks Online CSV for the selection (api#799). Never called with
   * nothing selected — the route reads an empty list as "every invoice", and
   * the button is not rendered then. A failure is a red toast with the API's
   * words, never a silent no-op.
   */
  const handleDownloadCsv = async () => {
    if (selectedVisible.length === 0) return;
    setCsvDownloading(true);
    try {
      const filename = await downloadInvoiceCsv(
        selectedVisible.map(inv => inv.id),
        selectedVisible.length === 1 ? selectedVisible[0].invoice_number : null,
      );
      toast.success(`Downloaded ${filename}`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to download the invoice CSV');
    } finally {
      setCsvDownloading(false);
    }
  };

  const selectCheckbox = (inv: Invoice) => (
    <input
      type="checkbox"
      aria-label={`Select ${inv.invoice_number || 'invoice'}`}
      checked={selectedIds.has(inv.id)}
      onChange={() => toggleSelected(inv.id)}
      className="size-4 cursor-pointer accent-fieldiq-accent"
    />
  );

  const qboPushButton = (inv: Invoice) =>
    inv.qbo_invoice_id ? (
      <span className="inline-flex items-center text-[10px] font-semibold text-green-400 px-1.5 py-0.5">✓ QBO</span>
    ) : perms.canPushQbo ? (
      // Disabled until the company is ready, INCLUDING while readiness loads
      // (qboPushGate). The reason is a line above the list, not a tooltip.
      <Tooltip content={pushGate.enabled ? 'Push to QuickBooks' : 'Push is off — see the note above the list'}>
        <button
          onClick={() => handlePushToQbo(inv.id)}
          disabled={pushingQbo === inv.id || !pushGate.enabled}
          aria-label="Push to QuickBooks"
          className="p-1.5 rounded-lg bg-green-600/20 text-green-400 hover:bg-green-600/40 disabled:opacity-50 transition-colors flex items-center gap-1 px-2"
        >
          {pushingQbo === inv.id ? (
            <span className="text-xs">Pushing...</span>
          ) : (
            <span className="text-xs font-semibold">QB</span>
          )}
        </button>
      </Tooltip>
    ) : null;

  // Every action below calls a casing /invoices route, so only a casing row has any.
  const rowActions = (inv: Invoice) => !isCasingRow(inv) ? null : (
    <>
      {activeTab === 'draft' && perms.canSend && (
        <Tooltip content="Send">
          <button
            onClick={() => handleAction(inv, 'send')}
            disabled={actingOn === inv.id}
            aria-label="Send"
            className="p-1.5 rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600/40 transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </Tooltip>
      )}
      {activeTab === 'sent' && (
        <>
          {perms.canRecordPayment && (
            <Tooltip content="Mark Paid">
              <button
                onClick={() => handleAction(inv, 'mark_paid')}
                disabled={actingOn === inv.id}
                aria-label="Mark Paid"
                className="p-1.5 rounded-lg bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/40 transition-colors"
              >
                <DollarSign className="w-4 h-4" />
              </button>
            </Tooltip>
          )}
          {qboPushButton(inv)}
        </>
      )}
      {activeTab === 'finalized' && qboPushButton(inv)}
      {activeTab === 'overdue' && (
        <>
          {perms.canSend && (
            <Tooltip content="Send Reminder">
              <button
                onClick={() => handleAction(inv, 'send')}
            disabled={actingOn === inv.id}
                aria-label="Send Reminder"
                className="p-1.5 rounded-lg bg-amber-600/20 text-amber-400 hover:bg-amber-600/40 transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </Tooltip>
          )}
          {qboPushButton(inv)}
        </>
      )}
    </>
  );

  return (
    <div data-testid="finance-invoices-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global header. Pages never do. */}
      <PageHeaderBand
        eyebrow="Accounts receivable"
        title="Invoices"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          /*
            The way IN is held with the modal itself — see
            MANUAL_INVOICE_CREATION_ENABLED. Two guards on one const, not
            one guard: a disabled-looking action the operator can still
            reach by a stale bundle, and a modal that can still be opened
            by a route or a test, are different holes. Neither is enough
            alone, and both read the same switch so they cannot drift.

            Withdrawn rather than shown greyed out: nobody has ever
            created a manual invoice (0 of 62 on production), so there is
            no habit to explain away, and a permanently dead button is
            its own kind of lie about what the app can do.

            And per role: POST /invoices is admin / office staff.
          */
          // NewInvoiceModal POSTs a CASING invoice, so a tenant without casing is not offered it.
          MANUAL_INVOICE_CREATION_ENABLED && perms.canCreate && showsCasing ? (
            <button
              type="button"
              onClick={() => setShowNewInvoice(true)}
              className="fi-action fi-action--standard fi-action--primary"
            >
              <Plus className="size-4" aria-hidden="true" />
              New Invoice
            </button>
          ) : undefined
        }
        /* The four figures /invoices/summary/dashboard returns, unchanged.
           `outstanding_total` is SUM(total_amount) over sent + overdue only —
           a Saved (`finalized`) invoice is NOT outstanding here, matching the
           dashboard. `paid_this_month` is paid_at in the current month. The
           third label is "Overdue": the circle's edge crosses the third column
           at 1024-1048px and the clearance was measured for labels no wider
           than Quotes' "Accepted". */
        stats={!showsCasing ? undefined : [
          {
            icon: <DollarSign className="size-4" aria-hidden="true" />,
            label: 'Outstanding',
            sub: 'Sent + overdue',
            tone: 'info',
            loading: summaryLoading,
            value: formatCurrency(summary?.outstanding_total),
          },
          {
            icon: <Clock className="size-4" aria-hidden="true" />,
            label: 'Drafts',
            sub: 'Not yet sent',
            tone: 'neutral',
            loading: summaryLoading,
            value: displayValue(summary?.draft_count),
          },
          {
            icon: <AlertTriangle className="size-4" aria-hidden="true" />,
            label: 'Overdue',
            sub: 'Past due date',
            tone: 'accent',
            loading: summaryLoading,
            value: displayValue(summary?.overdue_count),
          },
          {
            icon: <CheckCircle className="size-4" aria-hidden="true" />,
            label: 'Paid',
            sub: 'This month',
            tone: 'success',
            loading: summaryLoading,
            value: formatCurrency(summary?.paid_this_month),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* The Locations pattern (#686): the list stays, and one line says who can change it. */}
          {showsCasing && !perms.canSend && (
            <p data-testid="invoices-readonly-note" className="text-xs text-fieldiq-muted">
              Only an admin or office staff can create, send, or record payment on invoices.
            </p>
          )}

          {actionError && (
            <div
              role="alert"
              data-testid="invoice-action-error"
              className="flex items-start gap-3 rounded-fi-panel border border-red-500/30 bg-red-500/10 px-4 py-3"
            >
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-red-300">{actionError.title}</p>
                <p className="text-xs text-red-200/80 mt-1">{actionError.detail}</p>
              </div>
              <button
                type="button"
                onClick={() => setActionError(null)}
                aria-label="Dismiss error"
                className="shrink-0 rounded p-1 text-red-300/70 hover:text-red-200 hover:bg-red-500/20 transition-colors"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* The QuickBooks push is casing's; a tenant without casing has nothing to push. */}
          {showsCasing && perms.canPushQbo && !pushGate.enabled && (
            <QboPushOffReason gate={pushGate} className="block" />
          )}

          {qboRefusal && (
            <QboPushRefusalNotice
              refusal={qboRefusal}
              onOpenMapping={(kind) => navigate(qboMappingTabHref(kind))}
              onDismiss={() => setQboRefusal(null)}
            />
          )}

          {qboError && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-fi-panel border border-red-500/30 bg-red-500/10 px-4 py-3"
            >
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-red-300">{qboError.title}</p>
                <p className="text-xs text-red-200/80 mt-1">{qboError.detail}</p>
                {qboError.fault && (
                  <Link
                    to={QBO_RECONNECT_ROUTE}
                    className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-fieldiq-accent hover:underline"
                  >
                    {QBO_FAULT_CTA[qboError.fault]}
                  </Link>
                )}
              </div>
              <button
                type="button"
                onClick={() => setQboError(null)}
                aria-label="Dismiss QuickBooks error"
                className="shrink-0 rounded p-1 text-red-300/70 hover:text-red-200 hover:bg-red-500/20 transition-colors"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          )}

          {otherFailed && (
            <div
              role="alert"
              data-testid="invoice-other-verticals-error"
              className="flex items-start gap-3 rounded-fi-panel border border-red-500/30 bg-red-500/10 px-4 py-3"
            >
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm font-semibold text-red-300">
                {`Could not load ${otherVerticals.map(v => OTHER_BILLING_VERTICALS[v].label).join(', ')} invoices. The list below may be missing them.`}
              </p>
            </div>
          )}

          {/* Status chips. The counts are the lengths of six unpaginated
              /invoices?status= lists — honest tallies of rows the API
              returned, because no endpoint counts Saved. They
              are company-wide and do not follow the filters below, as before.
              While the counts are in flight none is claimed. */}
          <div className="scrollbar-hide flex gap-2 overflow-x-auto">
            {visibleTabs.map(tab => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    isActive
                      ? 'bg-fi-amber text-fi-amber-ink'
                      : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {tab.label}
                  {countsReady && (
                    <span
                      data-testid={`invoice-chip-count-${tab.key}`}
                      className={`tabular-nums ${isActive ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}
                    >
                      {displayValue(chipCount(tab.key))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            {verticalChips.map(chip => (
              <button
                key={chip.key}
                type="button"
                onClick={() => setVerticalFilter(chip.key)}
                className={`rounded-fi-pill border px-3 py-1.5 text-xs font-bold transition-colors ${
                  verticalFilter === chip.key
                    ? 'border-fi-amber bg-fieldiq-card text-fieldiq-text'
                    : 'border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                }`}
              >
                {chip.label}
              </button>
            ))}

            <select
              value={customerFilter}
              onChange={e => setCustomerFilter(e.target.value)}
              aria-label="Filter by customer"
              className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-sm text-white focus:border-fi-amber focus:outline-none"
            >
              <option value="">All Customers</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            <div className="flex items-center gap-1.5">
              <Calendar className="size-4 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                aria-label="From date"
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-2 py-1.5 text-sm text-white focus:border-fi-amber focus:outline-none"
              />
              <span className="text-fieldiq-muted text-xs">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                aria-label="To date"
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-2 py-1.5 text-sm text-white focus:border-fi-amber focus:outline-none"
              />
            </div>
          </div>

          {/* The register. The amber edge is on this panel and nowhere else;
              rows carry a status rail, never a second amber one. */}
          <section
            data-testid="invoice-register"
            aria-labelledby="invoice-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="flex flex-wrap items-center justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
              <div>
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Invoice register</p>
                <h2 id="invoice-register-title" className="mt-1 text-base font-extrabold tracking-tight text-white">
                  Billing documents
                </h2>
              </div>
              {selectable && selectedVisible.length > 0 && (
                <button
                  type="button"
                  onClick={handleDownloadCsv}
                  disabled={csvDownloading}
                  className="inline-flex items-center gap-1.5 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-sm font-semibold text-fieldiq-text transition-colors hover:border-fi-amber disabled:opacity-50"
                >
                  {csvDownloading
                    ? <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    : <Download className="size-4" aria-hidden="true" />}
                  Download CSV
                  <span className="tabular-nums text-fieldiq-muted">({selectedVisible.length})</span>
                </button>
              )}
            </header>

            {/* Loading skeleton */}
            {isLoading && (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            )}

            {/* Empty state */}
            {!isLoading && invoices.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-fieldiq-muted">
                <FileText className="w-12 h-12 mb-3 opacity-40" />
                <p className="text-sm font-medium">No invoices in this tab</p>
                <p className="text-xs mt-1 opacity-60">
                  {activeTab === 'all'
                    ? 'No invoices found with the current filters'
                    : `No ${activeTab.replace('_', ' ')} invoices found with the current filters`}
                </p>
              </div>
            )}

            {!isLoading && invoices.length > 0 && (
              <>
                {/* Wide viewports: the register in columns. */}
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[960px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      {/* Transparent 4px so the head lines up with the railed rows. */}
                      <tr className="border-l-4 border-l-transparent">
                        {selectable && (
                          <th scope="col" className="w-10 pl-4 py-3">
                            <input
                              type="checkbox"
                              aria-label="Select all invoices in this list"
                              checked={allVisibleSelected}
                              onChange={e => setSelectedIds(e.target.checked ? new Set(selectableRows.map(inv => inv.id)) : new Set())}
                              className="size-4 cursor-pointer accent-fieldiq-accent"
                            />
                          </th>
                        )}
                        <th scope="col" className="whitespace-nowrap px-4 py-3">Invoice #</th>
                        <th scope="col" className="px-4 py-3">Customer</th>
                        <th scope="col" className="px-4 py-3">Source</th>
                        <th scope="col" className="px-4 py-3 text-right">Total</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="px-4 py-3">Created</th>
                        <th scope="col" className="px-4 py-3">Actions</th>
                        <th scope="col" className="w-8" />
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map(inv => (
                        <tr
                          key={inv.id}
                          data-testid="invoice-row"
                          {...rowOpenProps(inv)}
                          className={`${inv.detail_path ? 'cursor-pointer ' : ''}border-t border-l-4 border-fieldiq-border outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${statusSkin(inv.status).rail}`}
                        >
                          {selectable && (
                            <td className="w-10 pl-4 py-3" onClick={e => e.stopPropagation()}>
                              {isCasingRow(inv) && selectCheckbox(inv)}
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="whitespace-nowrap text-sm font-extrabold text-fi-amber">{inv.invoice_number || '—'}</span>
                              {inv.ai_suggested && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-purple-500/20 text-purple-400 border border-purple-500/30 px-1.5 py-0.5 rounded">
                                  <Bot className="w-3 h-3" /> AI suggested
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="max-w-[280px] truncate px-4 py-3 text-sm font-bold text-white">
                            {inv.customer_name || '—'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {(inv.source_types || []).map((src, i) => (
                                <SourceBadge key={i} type={src} />
                              ))}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-extrabold text-white tabular-nums">
                            {formatCurrency(inv.total, inv.currency)}
                          </td>
                          <td className="px-4 py-3">
                            <InvoiceStatusBadge status={inv.status} />
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-muted">
                            {formatDate(inv.created_at, timeZone) || '—'}
                          </td>
                          <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                            <div className="flex gap-1.5">{rowActions(inv)}</div>
                          </td>
                          <td className="pr-3 py-3">
                            {inv.detail_path && <ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Narrow viewports: the same rows, stacked. No rail on a card. */}
                <div className="space-y-3 p-4 md:hidden">
                  {invoices.map(inv => (
                    <div
                      key={inv.id}
                      data-testid="invoice-card"
                      {...rowOpenProps(inv)}
                      className={`${inv.detail_path ? 'cursor-pointer ' : ''}rounded-fi-panel border border-fieldiq-border bg-fieldiq-card outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10`}
                    >
                      <div className="px-4 pb-3 pt-4">
                        <div className="mb-2 flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-2">
                            {selectable && isCasingRow(inv) && (
                              <span onClick={e => e.stopPropagation()} className="flex">
                                {selectCheckbox(inv)}
                              </span>
                            )}
                            <span className="text-sm font-extrabold text-fi-amber">{inv.invoice_number || '—'}</span>
                            {inv.ai_suggested && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-purple-500/20 text-purple-400 border border-purple-500/30 px-1.5 py-0.5 rounded">
                                <Bot className="w-3 h-3" /> AI
                              </span>
                            )}
                          </div>
                          <InvoiceStatusBadge status={inv.status} />
                        </div>
                        <h3 className="truncate text-base font-extrabold tracking-tight text-white">
                          {inv.customer_name || '—'}
                        </h3>
                        {(inv.source_types || []).length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {inv.source_types.map((src, i) => (
                              <SourceBadge key={i} type={src} />
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-3 border-t border-fieldiq-border px-4 py-3">
                        <span className="text-sm text-fieldiq-muted">{formatDate(inv.created_at, timeZone) || '—'}</span>
                        <span className="text-base font-extrabold text-white tabular-nums">{formatCurrency(inv.total, inv.currency)}</span>
                      </div>
                      <div className="flex gap-1.5 px-4 pb-3 empty:hidden" onClick={e => e.stopPropagation()}>
                        {rowActions(inv)}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </section>

      <NewInvoiceModal open={showNewInvoice} onOpenChange={setShowNewInvoice} />
    </div>
  );
}
